import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useMemo, useState } from "react";
import { useStudyGroupUnreadCounts } from "@/hooks/useStudyGroupUnreadCounts";
import { studyGroupsApi } from "@/lib/humhub-api/studyGroups";
import type {
  LightStudyGroupResponse,
  PaginatedResponse,
  StudyGroupUnreadTotals,
} from "@/lib/humhub-api/types";

interface StudyGroupsPaginatedResult {
  accumulatedGroups: LightStudyGroupResponse[];
  pagination:
    | {
        total: number;
        page: number;
        perPage: number;
        pages: number;
        links: unknown;
      }
    | undefined;
  isLoading: boolean;
  isLoadingMore: boolean;
  error: string | null;
  hasMore: boolean;
  loadMore: () => Promise<void>;
  refetch: () => void;
  resetToFirstPage: () => void;
  totalUnreadCounts: StudyGroupUnreadTotals;
}

export const useMyStudyGroupsPaginated = (
  perPage = 50,
  status?: string,
): StudyGroupsPaginatedResult => {
  const queryClient = useQueryClient();
  const [currentPage, setCurrentPage] = useState(1);
  const [loadedPages, setLoadedPages] = useState<Set<number>>(new Set([1]));
  const [isLoadingMore, setIsLoadingMore] = useState(false);
  const { totalUnreadCounts, refreshUnreadCounts } =
    useStudyGroupUnreadCounts();

  // 各ページのクエリキーを生成
  const getQueryKey = useCallback(
    (page: number) => ["myStudyGroups", { page, perPage, status }] as const,
    [perPage, status],
  );

  // 現在のページのクエリ
  const {
    data: currentPageData,
    isLoading: isCurrentPageLoading,
    error: queryError,
  } = useQuery({
    queryKey: getQueryKey(currentPage),
    queryFn: async () => {
      const response = await studyGroupsApi.getMyStudyGroups(
        currentPage,
        perPage,
        status,
      );
      try {
        await refreshUnreadCounts();
      } catch (error) {
        console.error("Failed to refresh unread study group counts:", error);
      }
      return response;
    },
    staleTime: 3 * 60 * 1000,
  });

  // 累積データを計算（修正版）
  const accumulatedGroups = useMemo(() => {
    const groups: LightStudyGroupResponse[] = [];
    const sortedPages = Array.from(loadedPages).sort((a, b) => a - b);

    for (const page of sortedPages) {
      let pageData: PaginatedResponse<LightStudyGroupResponse> | undefined;

      // 現在のページの場合は、useQueryから取得したデータを使用
      if (page === currentPage && currentPageData) {
        pageData = currentPageData;
      } else {
        // 他のページはキャッシュから取得
        pageData = queryClient.getQueryData<
          PaginatedResponse<LightStudyGroupResponse>
        >(getQueryKey(page));
      }

      if (pageData && typeof pageData === "object" && "results" in pageData) {
        const results = pageData.results;

        if (Array.isArray(results)) {
          groups.push(...results);
        }
      }
    }

    // 重複除去（IDベース）
    const uniqueGroups = groups.filter(
      (group, index, self) =>
        index === self.findIndex((g) => g.id === group.id),
    );

    return uniqueGroups;
  }, [loadedPages, queryClient, getQueryKey, currentPage, currentPageData]);

  // ページネーション情報
  const pagination = useMemo(() => {
    if (!currentPageData) return undefined;

    return {
      total: currentPageData.total,
      page: currentPageData.page,
      perPage,
      pages: currentPageData.pages,
      links: currentPageData.links,
    };
  }, [currentPageData, perPage]);

  // 次のページがあるかどうか
  const hasMore = useMemo(() => {
    return pagination ? currentPage < pagination.pages : false;
  }, [pagination, currentPage]);

  // 「もっと見る」機能
  const loadMore = useCallback(async () => {
    if (isLoadingMore || !hasMore) return;

    setIsLoadingMore(true);
    const nextPage = currentPage + 1;

    try {
      // 次のページのデータをプリフェッチ
      await queryClient.prefetchQuery({
        queryKey: getQueryKey(nextPage),
        queryFn: async () => {
          const response = await studyGroupsApi.getMyStudyGroups(
            nextPage,
            perPage,
            status,
          );
          try {
            await refreshUnreadCounts();
          } catch (error) {
            console.error(
              "Failed to refresh unread study group counts:",
              error,
            );
          }
          return response;
        },
        staleTime: 3 * 60 * 1000,
      });

      // 読み込んだページを記録
      setLoadedPages((prev) => new Set([...prev, nextPage]));
      setCurrentPage(nextPage);
    } finally {
      setIsLoadingMore(false);
    }
  }, [
    isLoadingMore,
    hasMore,
    currentPage,
    queryClient,
    getQueryKey,
    perPage,
    status,
    refreshUnreadCounts,
  ]);

  // 全体のリフレッシュ
  const refetch = useCallback(() => {
    // 読み込み済みのすべてのページを無効化
    Array.from(loadedPages).forEach((page) => {
      queryClient.invalidateQueries({ queryKey: getQueryKey(page) });
    });
  }, [loadedPages, queryClient, getQueryKey]);

  // 最初のページにリセット
  const resetToFirstPage = useCallback(() => {
    setCurrentPage(1);
    setLoadedPages(new Set([1]));
    // 1ページ目以外のキャッシュをクリア（statusを考慮）
    queryClient.removeQueries({
      predicate: (query) => {
        const key = query.queryKey;
        return (
          Array.isArray(key) &&
          key[0] === "myStudyGroups" &&
          typeof key[1] === "object" &&
          key[1] !== null &&
          "page" in key[1] &&
          "status" in key[1] &&
          (key[1] as { page: number; status?: string }).page > 1 &&
          (key[1] as { page: number; status?: string }).status === status
        );
      },
    });
  }, [queryClient, status]);

  return {
    accumulatedGroups,
    pagination,
    isLoading: isCurrentPageLoading && accumulatedGroups.length === 0,
    isLoadingMore,
    error: queryError ? String(queryError) : null,
    hasMore,
    loadMore,
    refetch,
    resetToFirstPage,
    totalUnreadCounts,
  };
};
